import { useSyncExternalStore } from "react";

/* ── Responsive breakpoint hooks ─────────────────────────────────────────────
   The single JS entry-point for "how wide is the viewport right now". Backed by
   matchMedia + useSyncExternalStore so a component only re-renders when it
   crosses a breakpoint boundary (not on every resized pixel), and the value is
   always consistent between render and commit.

   The scale lives here (not tokens.ts) so the hook has zero cross-module
   coupling. `mobile: 900` is the shell's rail↔drawer cutoff — the width where
   the left sidebar hands off to the hamburger drawer (dashboard.tsx CSS). */
export const BP = { sm: 640, md: 768, mobile: 900, lg: 1024, xl: 1280 } as const;
export type BreakpointKey = keyof typeof BP;

/** Subscribe/read a single media query. Re-renders only when it toggles. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      // Guard SSR / non-DOM environments — the dashboard is a client SPA, but
      // this keeps the hook safe if it's ever rendered without a window.
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    () => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false),
    () => false, // server snapshot — assume desktop (no match on max-width queries)
  );
}

/** True while the viewport is narrower than breakpoint `key` (max-width: key-1). */
export function useBelow(key: BreakpointKey): boolean {
  return useMediaQuery(`(max-width: ${BP[key] - 1}px)`);
}

/** True while the viewport is at least breakpoint `key` wide (min-width: key). */
export function useAtLeast(key: BreakpointKey): boolean {
  return useMediaQuery(`(min-width: ${BP[key]}px)`);
}

export interface Breakpoint {
  /** < md (768px) — the off-canvas drawer tier. */
  isMobile: boolean;
  /** md–lg (768–1023px) — tablet / heavily-zoomed desktop; icon-rail tier. */
  isTablet: boolean;
  /** ≥ lg (1024px) — full sidebar tier. */
  isDesktop: boolean;
  /** < lg (1024px) — mobile OR tablet; the "reclaim width" band. */
  belowLg: boolean;
}

/**
 * Resolve the current shell tier. Two matchMedia listeners (md, lg) drive three
 * tiers, so the shell re-renders only when it crosses 768 or 1024 — the two
 * boundaries where the sidebar changes shape (drawer ↔ rail ↔ full).
 */
export function useBreakpoint(): Breakpoint {
  const isMobile = useBelow("md"); // < 768
  const belowLg = useBelow("lg"); // < 1024
  return {
    isMobile,
    isTablet: !isMobile && belowLg,
    isDesktop: !belowLg,
    belowLg,
  };
}
